import { useCallback, useRef, useState } from 'react';
import { artifactoryService } from '../../../../services/artifactoryService';

interface Args {
  sessionId: string;
  reloadMessages: () => Promise<void>;
  seedCardSpineExpanded: (cardId: string) => void;
  fitView: (opts: any) => void;
}

/**
 * Source Spines SP-3b — canvas-level lifecycle for "Ask" on an artifact tile.
 * onAsk: in-flight guard → spawnArtifactSpine → await reloadMessages() (REQUIRED
 * on BOTH `created` values — a spawn that re-opens an existing spine doesn't
 * change state.messages.length, so a passive reload never fires) → seed-expand
 * the tile's spine → fit the camera on the tile. 403/404 → askError, no seed.
 */
export function useSpineSpawnControl({ sessionId, reloadMessages, seedCardSpineExpanded, fitView }: Args) {
  const inFlight = useRef(false);
  const [askInFlight, setAskInFlight] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  const onAsk = useCallback(async (artifactId: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setAskInFlight(true);
    setAskError(null);
    const tileId = `tile-${artifactId}`;
    try {
      await artifactoryService.spawnArtifactSpine(sessionId, artifactId);
      await reloadMessages();                 // REQUIRED on BOTH created values
      seedCardSpineExpanded(tileId);
      // Fit on the tile alone — the panel id (intent-panel-{rootMsgId}) isn't
      // known pre-reload; the tile is a sufficient camera anchor and exists now.
      fitView({ nodes: [{ id: tileId }], duration: 300, maxZoom: 1 });
    } catch (e: any) {
      const status = e?.response?.status ?? e?.status;
      setAskError(status === 403 ? 'Not allowed to chat about this artifact.'
        : status === 404 ? 'This artifact is no longer available.'
        : 'Couldn’t start the chat — please try again.');
    } finally {
      inFlight.current = false;
      setAskInFlight(false);
    }
  }, [sessionId, reloadMessages, seedCardSpineExpanded, fitView]);

  return { onAsk, askInFlight, askError, clearAskError: () => setAskError(null) };
}
